import * as XLSX from "xlsx";
import { resolveCodeForFramework } from "./exportHelpers";

const CATEGORY_ORDER = ["Environment", "Social", "Governance"];
const CATEGORY_SHEET = {
  Environment: "Environment",
  Social: "Social",
  Governance: "Governance",
};

const buildDisclosureMap = (disclosures = []) => {
  const map = new Map();
  disclosures.forEach((disclosure) => {
    if (!map.has(disclosure.code)) {
      map.set(disclosure.code, disclosure);
    }
  });
  return map;
};

const getDisclosureValue = (entries = [], labels) => {
  if (!entries || entries.length === 0) {
    return { value: "—", unit: "" };
  }

  if (entries.length > 1) {
    const numericValues = entries
      .map((entry) => Number(entry.value))
      .filter((value) => Number.isFinite(value));
    if (numericValues.length === entries.length && numericValues.length > 0) {
      const sum = numericValues.reduce((a, b) => a + b, 0);
      const unit = entries[0]?.valueUnit || entries[0]?.unit || "";
      return { value: sum.toLocaleString(), unit };
    }
    return { value: `${entries.length} ${labels.entries}`, unit: "" };
  }

  const entry = entries[0] || {};
  if (Array.isArray(entry.subEntries) && entry.subEntries.length > 0) {
    return { value: `${entry.subEntries.length} ${labels.items}`, unit: "" };
  }
  return {
    value: entry.value || "—",
    unit: entry.valueUnit || entry.unit || "",
  };
};

const buildCodesByCategory = (
  selectedCodesByCategory = {},
  delegatedDisclosures,
  disclosureMap,
) => {
  const result = CATEGORY_ORDER.reduce((acc, pillar) => {
    acc[pillar] = [...(selectedCodesByCategory[pillar] || [])];
    return acc;
  }, {});

  delegatedDisclosures?.forEach((_, code) => {
    const disclosure = disclosureMap.get(code);
    const category = disclosure?.category;
    if (category && result[category] && !result[category].includes(code)) {
      result[category].push(code);
    }
  });

  return result;
};

const buildSafeFileName = (name) => {
  return name.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "_");
};

const downloadWorkbook = (workbook, fileName) => {
  const arrayBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });
  const blob = new Blob([arrayBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const exportESGReportToExcel = ({
  reportName,
  framework,
  reportYear,
  orgName,
  progressPercentage,
  completedCount,
  selectedCodesByCategory,
  availableDisclosures,
  customDisclosures,
  entriesByCode,
  isDisclosureCompleted,
  delegatedDisclosures,
  exportFramework = "All",
  metricMappings = [],
  t,
}) => {
  const disclosureMap = buildDisclosureMap([
    ...availableDisclosures,
    ...customDisclosures,
  ]);

  const labels = {
    entries: t("entries"),
    items: t("items"),
  };

  const codesByCategory = buildCodesByCategory(
    selectedCodesByCategory,
    delegatedDisclosures,
    disclosureMap,
  );

  const rowsByCategory = {
    Environment: [],
    Social: [],
    Governance: [],
  };
  CATEGORY_ORDER.forEach((pillar) => {
    const codes = codesByCategory[pillar] || [];
    codes.forEach((code) => {
      const disclosure = disclosureMap.get(code) || {};
      const resolved = resolveCodeForFramework(
        code,
        disclosure,
        exportFramework,
        metricMappings,
      );
      if (!resolved) return;

      const entries = entriesByCode.get(code) || [];
      const proofsCount = entries.reduce(
        (sum, entry) => sum + (entry.proofs?.length || 0),
        0,
      );
      const { value, unit } = getDisclosureValue(entries, labels);
      const delegated = delegatedDisclosures?.get(code);
      const completed = isDisclosureCompleted(code);
      const status = completed
        ? t("Completed")
        : delegated
          ? t("Delegated")
          : t("Pending");

      rowsByCategory[pillar].push({
        [t("Pillar")]: t(pillar),
        [t("Code")]: resolved.code,
        [t("Disclosure")]: resolved.title,
        [t("Value")]: value,
        [t("Unit")]: unit,
        [t("Status")]: status,
        [t("Entries")]: entries.length,
        [t("Proofs")]: proofsCount,
        [t("Delegated To")]: delegated?.email || "",
      });
    });
  });

  const totalDisclosures = CATEGORY_ORDER.reduce(
    (sum, pillar) => sum + rowsByCategory[pillar].length,
    0,
  );
  const summaryRows = [
    [t("Field"), t("Value")],
    [t("Report Name"), reportName || `${framework} ${t("Report")}`],
    [t("Framework"), framework],
    [t("Reporting Year"), reportYear],
    [t("Organization"), orgName || "-"],
    [t("Total Disclosures"), totalDisclosures],
    [t("Completed"), completedCount],
    [t("Completion Percentage"), `${progressPercentage}%`],
  ];

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
  CATEGORY_ORDER.forEach((pillar) => {
    const sheetName = CATEGORY_SHEET[pillar] || pillar;
    const sheet = XLSX.utils.json_to_sheet(rowsByCategory[pillar]);
    XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
  });

  const dateSuffix = new Date().toISOString().slice(0, 10);
  const safeParts = [reportName || "ESG_Report", framework, orgName, dateSuffix]
    .filter(Boolean)
    .map(buildSafeFileName);
  const fileName = `${safeParts.join("_")}.xlsx`;

  downloadWorkbook(workbook, fileName);
  return fileName;
};
